import { ChatSettingsPanel } from '@/components/chat/ChatSettingsPanel'
import { cn } from '@/lib/utils'
import type { SessionSettings } from '@/types/chat'

interface ChatSettingsSheetProps {
  open: boolean
  onClose: () => void
  settings: SessionSettings
  systemPrompt: string
  onSettingsChange: (patch: Partial<SessionSettings>) => void
  onSystemPromptChange: (value: string) => void
}

export function ChatSettingsSheet({
  open,
  onClose,
  settings,
  systemPrompt,
  onSettingsChange,
  onSystemPromptChange,
}: ChatSettingsSheetProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50" data-testid="chat-settings-sheet">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close settings" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-auto rounded-t-2xl border border-border bg-card shadow-xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold">Session Settings</h2>
          <button type="button" className="text-sm text-primary" onClick={onClose}>
            Done
          </button>
        </div>
        <ChatSettingsPanel
          settings={settings}
          systemPrompt={systemPrompt}
          onSettingsChange={onSettingsChange}
          onSystemPromptChange={onSystemPromptChange}
          useTextareaFallback
          className="border-0"
        />
      </div>
    </div>
  )
}

interface CollapsibleSettingsProps {
  collapsed: boolean
  onToggle: () => void
  children: React.ReactNode
}

export function CollapsibleSettings({ collapsed, onToggle, children }: CollapsibleSettingsProps) {
  return (
    <div className="border-b border-border">
      <button
        type="button"
        className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium"
        onClick={onToggle}
        data-testid="chat-settings-toggle"
      >
        Settings
        <span>{collapsed ? 'Show' : 'Hide'}</span>
      </button>
      {!collapsed ? <div className="max-h-64 overflow-auto">{children}</div> : null}
    </div>
  )
}

export function ChatSettingsOverlay({
  open,
  onClose,
  children,
  className,
}: {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
}) {
  if (!open) return null

  return (
    <div className={cn('absolute inset-y-0 right-0 z-20 w-80 border-l border-border bg-card shadow-lg', className)}>
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-sm font-semibold">Settings</span>
        <button type="button" className="text-xs text-primary" onClick={onClose}>
          Close
        </button>
      </div>
      {children}
    </div>
  )
}
